/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Container,
  Skeleton,
  FormControl,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { GuaranteeConfigStruture, TaxSystemConfigResponse } from "@interfaces/index";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import { CustomDialog } from "@components/CustomDialog";
import { StyledSlider } from "@components/CustomSlider";
import { ButtonTypeSelected } from "@components/ButtonTypeSelected";
import IconInfo from "@components/Images/IconInfo";
import { useLazyGetGuaranteeConfigQuery } from "@api/catalogs";
import { GUARANTEE_TYPE } from "@helpers/constantes";
import * as yup from "yup";
import { useFormik } from "formik";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

const CotizadorAvanzaSolido = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height:820px)");
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAE, setConfigPFAE] = useState<TaxSystemConfigResponse | null>(null);
  const [personType, setPersonType] = useState<string>("PM");

  const [guaranteeType, setGuaranteeType] = useState<string>("");

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string | JSX.Element }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [guaranteeConfigListPM, setGuaranteeConfigListPM] = useState<GuaranteeConfigStruture[]>([]);
  const [guaranteeConfigListPFAE, setGuaranteeConfigListPFAE] = useState<GuaranteeConfigStruture[]>([]);

  const [guaranteeConfig, setGuaranteeConfig] = useState<GuaranteeConfigStruture[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
  });

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [hideBottom, setHideBottom] = useState<boolean>(false);
  const [hoverDialogOpen, setHoverDialogOpen] = useState(false);

  const handleOpenModal = () => setHoverDialogOpen(true);
  const handleCloseModal = () => setHoverDialogOpen(false);

  const formik = useFormik({
    initialValues: {
      description_guarantee: "",
    },
    validationSchema: yup.object().shape({
      description_guarantee: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values) => {
      console.log(values);
      setConditions();
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [responsePM, responsePFAE, responseGuaranteeConfigPM, responseGuaranteeConfigPFAE] = await Promise.all([
          getTaxSystemConfig("CSA01").unwrap(),
          getTaxSystemConfig("CSA02").unwrap(),
          getGuaranteeTypeConfig({ tax_code: "CSA01" }).unwrap(),
          getGuaranteeTypeConfig({ tax_code: "CSA02" }).unwrap(),
        ]);
        setConfigPM(responsePM);
        setConfigPFAE(responsePFAE);
        setGuaranteeConfigListPM(responseGuaranteeConfigPM);
        setGuaranteeConfigListPFAE(responseGuaranteeConfigPFAE);
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (personType === "PM" && configPM) {
      setCotizadorConfig(configPM);
    } else if (personType === "PFAE" && configPFAE) {
      setCotizadorConfig(configPFAE);
    }
  }, [personType, configPM, configPFAE]);

  useEffect(() => {
    if (personType === "PM" && guaranteeConfigListPM) {
      setGuaranteeConfig(guaranteeConfigListPM);
    } else if (personType === "PFAE" && guaranteeConfigListPFAE) {
      setGuaranteeConfig(guaranteeConfigListPFAE);
    }
    setGuaranteeType("");
  }, [personType, guaranteeConfigListPM, guaranteeConfigListPFAE]);

  useEffect(() => {
    const { minAmount, maxAmount, stepAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } =
      cotizadorConfig;

    if (minAmount < maxAmount && stepAmount > 0) {
      const marks = [];

      // Primer mark (inicio normal)
      marks.push({ value: minAmount, label: amountToText(minAmount) });

      // Último mark (aplicamos el estilo al label)
      marks.push({
        value: maxAmount,
        label: (
          <span
            style={{
              position: "relative",
              left: "-16px", // mueve hacia la izquierda
              whiteSpace: "nowrap", // evita que se corte en dos líneas
              maxWidth: "100%", // evita desbordamiento
              display: "inline-block", // necesario para que left funcione
            }}
          >
            {amountToText(maxAmount)}
          </span>
        ),
      });

      setAmountMarks(marks);
    }

    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];

      for (let i = minTerm; i <= maxTerm; i += 3) {
        marks.push({ value: i, label: `${i}` });
      }

      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setSelectedTerm(defaultTerm);
    }
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      const monthly = calculateAmountMonthly(selectedAmount, selectedTerm, interfaceConfig?.interest_rate);
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate || !guaranteeType) {
      return;
    }
  const sourceCode = sessionStorage.getItem("source_code");
    const selectedGuarantee = guaranteeConfig.find(
      (item) => item.guarantee_type.code === guaranteeType,
    )?.guarantee_type_id;

    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}&guarantee_type_id=${selectedGuarantee}&description_guarantee=${formik.values.description_guarantee}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const sourceCode = params.get("code");
      if (sourceCode) {
        sessionStorage.setItem("source_code", sourceCode);
      } else {
        sessionStorage.removeItem("source_code");
      }
    }, []);
  

  useEffect(() => {
    if (selectedAmount > 500000) {
      setHideBottom(true);
      setGuaranteeType(GUARANTEE_TYPE.grantia_hipotecaria.code);
    } else {
      setHideBottom(false);
    }
  }, [selectedAmount]);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <Container sx={{ mt: 2, mb: 2 }} maxWidth={"sm"}>
            <Typography fontWeight={500} variant="h3" mt={1} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Cotizador
            </Typography>

            <Box>
              <Box mt={1} ml={isMobileOrTablet ? 3 : 0} mr={isMobileOrTablet ? 3 : 0}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mt={1}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu régimen fiscal
                </Typography>

                <Grid container spacing={isMobileOrTablet ? 3 : 2} mt={1}>
                  <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-end"}>
                    {!isLoading ? (
                      <ButtonTypeSelected
                        title="Persona Moral"
                        onClick={() => setPersonType("PM")}
                        isSelected={personType === "PM"}
                        isMovile={isMobileOrTablet}
                        height={isShortScreen ? "60px" : "70px"}
                        heightMoviles="60px"
                        width={isShortScreen ? "170px" : "190px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={isShortScreen ? "12px" : "14px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-start"}>
                    {!isLoading ? (
                      <ButtonTypeSelected
                        title="Persona física con actividad empresarial"
                        onClick={() => setPersonType("PFAE")}
                        isSelected={personType === "PFAE"}
                        isMovile={isMobileOrTablet}
                        height={isShortScreen ? "60px" : "70px"}
                        heightMoviles="60px"
                        width={isShortScreen ? "170px" : "190px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={isShortScreen ? "12px" : "14px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box mt={1} ml={isMobileOrTablet ? 5 : 1} mr={isMobileOrTablet ? 3 : 0}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mt={1}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el monto
                </Typography>

                <Typography
                  fontWeight={700}
                  fontSize={"13px"}
                  mt={1}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  {amountToText(selectedAmount)}
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box ml={isMobileOrTablet ? 4 : 1} mr={isMobileOrTablet ? 3 : 0}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu tipo de Garantía{" "}
                  <IconInfo
                    fill={theme.palette.primary.light}
                    onClick={handleOpenModal}
                    style={{ cursor: "pointer" }}
                  />
                </Typography>

                <Grid container spacing={isMobileOrTablet ? 3 : 2} mt={0.5}>
                  {!hideBottom && (
                    <>
                      <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-end"}>
                        {!isLoading ? (
                          <ButtonTypeSelected
                            title="Garantía Prendaria"
                            onClick={() => {
                              setGuaranteeType(GUARANTEE_TYPE.grantia_prendaria.code);
                            }}
                            isSelected={guaranteeType === GUARANTEE_TYPE.grantia_prendaria.code}
                            isMovile={isMobileOrTablet}
                            height={isShortScreen ? "60px" : "70px"}
                            heightMoviles="60px"
                            width={isShortScreen ? "170px" : "190px"}
                            widthMoviles="130px"
                            icon={icon_check}
                            width_icon="22px"
                            height_icon="22px"
                            font_size={isShortScreen ? "12px" : "14px"}
                            top_icon="-12px"
                            font_size_movil="11px"
                          />
                        ) : (
                          <Skeleton
                            variant="rectangular"
                            animation="wave"
                            width={isMobileOrTablet ? 150 : 190}
                            height={isMobileOrTablet ? 60 : 70}
                          />
                        )}
                      </Grid>
                    </>
                  )}

                  <Grid
                    item
                    xs={!hideBottom ? 6 : 12}
                    md={!hideBottom ? 6 : 12}
                    display="flex"
                    justifyContent={isMobileOrTablet ? "center" : hideBottom ? "center" : "flex-start"}
                  >
                    {!isLoading ? (
                      <ButtonTypeSelected
                        title="Garantía Hipotecaria"
                        onClick={() => {
                          setGuaranteeType(GUARANTEE_TYPE.grantia_hipotecaria.code);
                        }}
                        isSelected={guaranteeType === GUARANTEE_TYPE.grantia_hipotecaria.code}
                        isMovile={isMobileOrTablet}
                        height={isShortScreen ? "60px" : "70px"}
                        heightMoviles="60px"
                        width={isShortScreen ? "170px" : "190px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={isShortScreen ? "12px" : "14px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
              <form onSubmit={formik.handleSubmit} noValidate>
                <Box ml={isMobileOrTablet ? 4 : 1} mr={isMobileOrTablet ? 3 : 0}>
                  <FormControl fullWidth>
                    <TextField
                      id="description_guarantee"
                      name="description_guarantee"
                      label="Describe tu garantía"
                      margin="normal"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.description_guarantee}
                      error={formik.touched.description_guarantee && Boolean(formik.errors.description_guarantee)}
                      helperText={formik.touched.description_guarantee ? formik.errors.description_guarantee : ""}
                      inputProps={{ maxLength: 150 }}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <Typography
                    fontWeight={500}
                    fontSize={"16px"}
                    mb={2}
                    color={theme?.palette?.primary?.dark}
                    textAlign={"center"}
                  >
                    Selecciona el plazo en meses
                  </Typography>

                  {!isLoading ? (
                    <StyledSlider
                      aria-label="term"
                      min={cotizadorConfig.minTerm}
                      max={cotizadorConfig.maxTerm}
                      step={1}
                      defaultValue={cotizadorConfig.defaultTerm}
                      marks={termMarks}
                      value={selectedTerm}
                      onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                      valueLabelDisplay="on"
                    />
                  ) : (
                    <Skeleton variant="rectangular" animation="wave" />
                  )}
                </Box>

                <Typography
                  fontWeight={700}
                  fontSize={"16px"}
                  mb={1}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  Pago mensual estimado*
                </Typography>

                <Typography
                  mb={1}
                  textAlign={"center"}
                  variant="h4"
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                  <Typography
                    variant="h5"
                    component={"span"}
                    fontWeight={theme?.palette?.fontWeights?.semibold}
                    color={theme?.palette?.primary?.dark}
                  >
                    MXN
                  </Typography>
                </Typography>

                <Box display="flex" justifyContent="center">
                  <Button variant="contained" type="submit" disabled={isLoading}>
                    Continuar
                  </Button>
                </Box>
              </form>
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <CustomDialog
        colorClose={theme?.palette?.primary?.main}
        open={hoverDialogOpen}
        onClose={handleCloseModal}
        itemTitle={""}
        itemContent={
          <>
            <Typography fontWeight={700} fontSize={"16px"} color={theme?.palette?.primary?.main} textAlign={"center"}>
              Garantía Prendaria
            </Typography>
            <Typography fontWeight={300} fontSize={"16px"} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Garantía 3:1
              <br />
              Activos Fijos y Bienes Muebles
            </Typography>
            <hr style={{ border: `1px solid ${theme?.palette?.primary?.main}` }} />
            <Typography fontWeight={700} fontSize={"16px"} color={theme?.palette?.primary?.main} textAlign={"center"}>
              Garantía Inmobiliaria
            </Typography>

            <Typography fontWeight={300} fontSize={"16px"} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Garantía 3:1
              <br />
              Bienes Inmuebles
            </Typography>
          </>
        }
      />
    </>
  );
};
export default CotizadorAvanzaSolido;
